export const REFRESH_MARGIN_MS = 60_000

type FetchedToken = { token: string; expiresAt: string }

type ChatTokenStoreDependencies = {
  fetchToken: () => Promise<FetchedToken>
  now?: () => number
}

/**
 * Keeps the chat token in memory only. It hands out the current token, gets a
 * new one when less than a minute of life remains, and shares one request
 * between callers that ask at the same time.
 */
export function createChatTokenStore({ fetchToken, now = Date.now }: ChatTokenStoreDependencies) {
  let current: { token: string; expiresAtMs: number } | null = null
  let inflight: Promise<string> | null = null

  async function load() {
    const { token, expiresAt } = await fetchToken()

    current = { token, expiresAtMs: Date.parse(expiresAt) }

    return token
  }

  return {
    getToken(): Promise<string> {
      if (current && current.expiresAtMs - now() > REFRESH_MARGIN_MS) {
        return Promise.resolve(current.token)
      }

      inflight ??= load().finally(() => {
        inflight = null
      })

      return inflight
    },

    invalidate() {
      current = null
    },
  }
}

export type ChatTokenStore = ReturnType<typeof createChatTokenStore>
